import { useState } from 'react'
import { de } from 'date-fns/locale'
import { CalendarIcon, Download, Printer } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  fromCalendarDate,
  toCalendarDate,
  useRangePick,
} from './date-column-picker'
import type { ReactNode } from 'react'
import type { Restaurant } from '@/api/types'
import type { DateColumn } from '@/features/reports/lib/order-proposal-math'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { AccountingLocationPicker } from '@/features/reports/components/accounting-location-picker'
import { formatColumn } from '@/features/reports/lib/order-proposal-labels'

const ALL = 'all'

/** The server's limit for the order days (D6). */
const MAX_ORDER_DAYS = 14

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

/** Standort, Liefertag, Hauptgruppe, the Tagesgerichte switch, CSV and Drucken. */
export function OrderProposalToolbar({
  restaurants,
  restaurantId,
  onRestaurantChange,
  delivery,
  onDeliveryChange,
  mainCategories,
  mainCategoryId,
  onMainCategoryChange,
  showTagesgerichte,
  onShowTagesgerichteChange,
  hideZeroRows,
  onHideZeroRowsChange,
  onCsv,
  csvDisabled,
  actions,
}: {
  restaurants: Array<Restaurant>
  restaurantId: number | null
  onRestaurantChange: (id: number) => void
  /** The order days: a single Liefertag, or a range for a delivery that must last several days. */
  delivery: DateColumn
  onDeliveryChange: (delivery: DateColumn) => void
  mainCategories: Array<{ id: number; name: string }>
  mainCategoryId: number | null
  onMainCategoryChange: (id: number | null) => void
  showTagesgerichte: boolean
  onShowTagesgerichteChange: (show: boolean) => void
  hideZeroRows: boolean
  onHideZeroRowsChange: (hide: boolean) => void
  onCsv: () => void
  csvDisabled: boolean
  /** Further actions beside the exports, e.g. "Bestellung senden". */
  actions?: ReactNode
}) {
  const { t } = useTranslation('reports')
  const [dayOpen, setDayOpen] = useState(false)
  // Like a date column: a day, a second click makes a range; it applies when the popover closes.
  const range = useRangePick(MAX_ORDER_DAYS)
  const closeDay = (next: boolean) => {
    setDayOpen(next)
    const picked = next ? null : range.take()
    if (picked) onDeliveryChange(picked)
  }
  const shown = range.pending ?? delivery

  return (
    <div className="flex flex-wrap items-end gap-3" data-print-hide>
      <Field label={t('orderProposal.location')}>
        <AccountingLocationPicker
          restaurants={restaurants}
          value={restaurantId}
          onChange={onRestaurantChange}
        />
      </Field>
      <Field label={t('orderProposal.deliveryDay')}>
        <Popover open={dayOpen} onOpenChange={closeDay}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              aria-label={t('orderProposal.deliveryDay')}
              className="h-12 justify-start gap-2 border-primary/40 font-semibold text-primary sm:h-9"
            >
              <CalendarIcon className="h-4 w-4" aria-hidden="true" />
              {formatColumn(delivery)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              captionLayout="dropdown"
              showWeekNumber
              selected={{
                from: toCalendarDate(shown.from),
                to: toCalendarDate(shown.to),
              }}
              onSelect={(_range, triggerDate) =>
                range.pick(fromCalendarDate(triggerDate))
              }
              locale={de}
              weekStartsOn={1}
              defaultMonth={toCalendarDate(shown.from)}
              startMonth={new Date(2020, 0)}
              endMonth={new Date(new Date().getFullYear() + 1, 11)}
            />
          </PopoverContent>
        </Popover>
      </Field>
      <Field label={t('orderProposal.mainCategory')}>
        <Select
          value={mainCategoryId == null ? ALL : String(mainCategoryId)}
          onValueChange={(value) =>
            onMainCategoryChange(value === ALL ? null : Number(value))
          }
        >
          <SelectTrigger
            aria-label={t('orderProposal.mainCategory')}
            className="h-12 w-full sm:h-9 sm:w-44"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>
              {t('orderProposal.allMainCategories')}
            </SelectItem>
            {mainCategories.map((category) => (
              <SelectItem key={category.id} value={String(category.id)}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <label className="flex h-12 items-center gap-2 text-sm sm:h-9">
        <Switch
          checked={showTagesgerichte}
          onCheckedChange={onShowTagesgerichteChange}
        />
        {t('orderProposal.tagesgerichte')}
      </label>
      <label className="flex h-12 items-center gap-2 text-sm sm:h-9">
        <Switch checked={hideZeroRows} onCheckedChange={onHideZeroRowsChange} />
        {t('orderProposal.hideZeroRows')}
      </label>
      <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
        <Button
          variant="outline"
          className="h-12 sm:h-9"
          onClick={onCsv}
          disabled={csvDisabled}
        >
          <Download className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('exportCsv')}
        </Button>
        <Button
          variant="outline"
          className="h-12 sm:h-9"
          onClick={() => window.print()}
        >
          <Printer className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('print')}
        </Button>
        {actions}
      </div>
    </div>
  )
}
